import { usePageMeta } from "@/hooks/usePageMeta";
const stats = [
  { v: "7k → 100k", l: "Instagram, highest in fintech industry" },
  { v: "5k → 130k+", l: "Twitter, industry avg. was 70k" },
  { v: "4–5%", l: "Engagement rate vs benchmark under 1%" },
  { v: "2M+", l: "Views, Sima campaign · 1 week delivery" },
];

const beliefs = [
  {
    n: "01",
    t: "Zero to one is a speciality",
    c: "You don't need to be an established brand to work with me. If you're still figuring out who you are, that's exactly where I start.",
  },
  {
    n: "02",
    t: "Context is everything",
    c: "A brand's story isn't just what it says. It's where, when, and to whom. Finding the right context is half the work.",
  },
  {
    n: "03",
    t: "Trust is demonstrated, not claimed",
    c: "The brands I admire don't tell people to trust them. They show up consistently and honestly, with something real to say.",
  },
  {
    n: "04",
    t: "Systems outlast campaigns",
    c: "A great campaign gets attention. A great brand system keeps it. I build both, but the system always comes first.",
  },
];

const skills = [
  {
    title: "Brand strategy",
    items: ["Positioning & narrative", "Voice, tone & messaging", "Audience & category mapping", "Brand systems & architecture"],
  },
  {
    title: "Campaign leadership",
    items: ["Insight-led concepts", "Influencer strategy & execution", "Cross-platform amplification", "Stakeholder + PR coordination"],
  },
  {
    title: "Social & content",
    items: ["Channel strategy from zero", "Editorial & content calendars", "Community management", "ORM & crisis frameworks"],
  },
  {
    title: "AI-assisted production",
    items: ["AI campaign visuals", "Product photography at scale", "Amazon listings & A+ content", "Writing systems & SEO craft"],
  },
];

export default function About() {
  usePageMeta({
    title: "About — Shivani | Brand Strategist",
    description: "Brand strategist who grew Fi Money's Instagram from 7k to 100k and Twitter from 5k to 130k+. Specialist in zero-to-one brand building.",
    ogTitle: "About — Shivani | Brand Strategist",
    ogDescription: "Zero-to-one brand specialist. Grew Fi Money's social from scratch to the highest in fintech.",
  });
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pt-16 md:pt-24 pb-12">
        <p className="eyebrow mb-8">About</p>
        <h1
          className="serif-display leading-[0.95] max-w-[22ch]"
          style={{ fontSize: "clamp(2.75rem, 6.8vw, 6.25rem)" }}
          data-testid="about-headline"
        >
          Some people inherit a brand. <span className="serif-italic">I&apos;ve always had to build one.</span>
        </h1>
      </section>

      {/* OPENING */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 pb-20 md:pb-28">
        <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="md:col-span-5">
            <img
              src="https://customer-assets.emergentagent.com/job_shivani-studio/artifacts/5zw1i64b_ChatGPT%20Image%20May%2023%2C%202026%2C%2004_55_42%20PM.png"
              alt="Portrait of Shivani Sharma"
              className="block w-full h-auto"
              style={{ aspectRatio: "4 / 5", objectFit: "cover", objectPosition: "center top" }}
              data-testid="about-portrait"
            />
          </div>
          <div className="md:col-span-7 md:pt-2">
            <p className="eyebrow mb-6" style={{ color: "rgba(26,26,26,0.55)" }}>
              Opening
            </p>
            <p
              className="serif-display leading-[1.18]"
              style={{ fontSize: "clamp(1.5rem, 2.6vw, 2.1rem)" }}
            >
              My career has been spent in rooms where there was no playbook. Just a brief, a blank
              slate, and the expectation that something real would exist on the other side. I&apos;ve
              learned to be comfortable there. <span className="serif-italic">More than comfortable, actually. It&apos;s where I do my best work.</span>
            </p>
          </div>
        </div>
      </section>

      {/* THE WORK THAT SHAPED ME */}
      <section
        className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28"
        style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}
      >
  
        <div className="grid md:grid-cols-12 gap-10 md:gap-14">
          <div className="md:col-span-4">
            <p className="eyebrow mb-6" style={{ color: "rgba(26,26,26,0.55)" }}>
              The work that shaped me
            </p>
            <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
              Built from zero <span className="serif-italic">at Fi.</span>
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-6 text-base md:text-lg leading-relaxed">
            <p>
              When I joined Fi Money in 2021, there was no brand. No community, no channels, no
              content, and no product yet either. Just a founding team with a vision for what a
              smarter, more honest neobank could look like for Indian millennials.
            </p>
            <p>
              Over two years, I built it from the ground up. I set up every social channel. I wrote
              the first posts, found the first followers, and figured out what this brand sounded
              like before anyone had written it down. I built the playbooks, the ORM systems, the
              crisis frameworks, and ran campaigns, including a 2M+ view influencer campaign with
              Sima Taparia, that earned attention without ever losing the brand&apos;s credibility.
            </p>
            <p>
              By the time I left, Fi Money had grown from nothing into one of the most engaged
              fintech brands on Indian social media. <span className="serif-italic">That&apos;s the work I&apos;m most proud of, not because of the numbers, but because of what it required to get there.</span>
            </p>
          </div>
        </div>

        {/* STATS STRIP */}
        <ul
          className="mt-16 md:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-0"
          data-testid="about-stats"
        >
          {stats.map((s, i) => (
            <li
              key={s.l}
              className="p-7 md:p-9"
              style={{
                border: "0.5px solid rgba(26,26,26,0.45)",
                marginLeft: i % 4 !== 0 ? "-0.5px" : "0",
                marginTop: i >= 2 && i < 4 ? "0" : "0",
              }}
            >
              <p className="serif-display text-3xl md:text-4xl lg:text-5xl leading-none">{s.v}</p>
              <p
                className="mt-4 text-[10px] font-sans font-medium uppercase tracking-[0.2em] leading-snug"
                style={{ color: "rgba(26,26,26,0.6)" }}
              >
                {s.l}
              </p>
            </li>
          ))}
        </ul>

        <div className="mt-12 md:mt-14">
          <div className="rule mb-6" style={{ opacity: 0.5 }} />
          <a
            href="https://www.socialsamosa.com/2021/11/fi-social-media-strategy/"
            target="_blank"
            rel="noreferrer"
            className="editorial-link inline-block text-sm"
            data-testid="about-fi-socialsamosa-link"
          >
            Read how Fi built trust on social →
          </a>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
        <div className="grid md:grid-cols-12">
          <blockquote
            className="md:col-span-10 md:col-start-2 pl-6 md:pl-10 serif-italic font-serif leading-[1.15]"
            style={{
              borderLeft: "0.5px solid #1A1A1A",
              fontSize: "clamp(1.85rem, 4vw, 3.25rem)",
            }}
          >
            Growth doesn&apos;t come from shouting louder. It comes from earning trust through
            clarity, credibility, and consistent storytelling.
          </blockquote>
        </div>
      </section>

      {/* WHAT I BELIEVE */}
      <section
        className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28"
        style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}
      >
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-4">
            <p className="eyebrow mb-6" style={{ color: "rgba(26,26,26,0.55)" }}>
              What I believe
            </p>
            <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
              Four working <span className="serif-italic">beliefs.</span>
            </h2>
          </div>
        </div>

        <ul className="grid sm:grid-cols-2 gap-0" data-testid="about-beliefs">
          {beliefs.map((b, i) => (
            <li
              key={b.n}
              className="p-8 md:p-10"
              style={{
                border: "0.5px solid rgba(26,26,26,0.45)",
                marginLeft: i % 2 === 1 ? "-0.5px" : "0",
                marginTop: i >= 2 ? "-0.5px" : "0",
              }}
            >
              <span
                className="serif-display text-4xl leading-none"
                style={{ color: "rgba(26,26,26,0.4)" }}
              >
                {b.n}
              </span>
              <h3 className="serif-display text-2xl md:text-3xl leading-tight mt-4 mb-5">
                {b.t}
              </h3>
              <p
                className="text-[0.95rem] md:text-base leading-relaxed"
                style={{ color: "rgba(26,26,26,0.78)" }}
              >
                {b.c}
              </p>
            </li>
          ))}
        </ul>
      </section>

      {/* SKILLS GRID */}
      <section
        className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28"
        style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}
      >
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-4">
            <p className="eyebrow mb-6" style={{ color: "rgba(26,26,26,0.55)" }}>
              Across the desk
            </p>
            <h2 className="serif-display text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
              What I actually <span className="serif-italic">do.</span>
            </h2>
          </div>
          <p
            className="md:col-span-7 md:col-start-6 md:pt-4 text-base md:text-lg leading-relaxed"
            style={{ color: "rgba(26,26,26,0.78)" }}
          >
            Four overlapping practices, one operating mode. Strategy on the page, in the room,
            and on the channel where it actually has to land.
          </p>
        </div>

        <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-14">
          {skills.map((g) => (
            <li key={g.title}>
              <h3
                className="serif-display text-2xl md:text-[1.65rem] leading-tight pb-3 mb-5"
                style={{ borderBottom: "0.5px solid rgba(26,26,26,0.5)" }}
              >
                {g.title}
              </h3>
              <ul className="space-y-2 text-[0.95rem]" style={{ color: "rgba(26,26,26,0.78)" }}>
                {g.items.map((it) => (
                  <li key={it} className="flex items-baseline gap-3">
                    <span className="serif-italic text-lg leading-none">—</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </section>

      {/* KRITI TESTIMONIAL */}
      <section
        className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28"
        style={{ borderTop: "0.5px solid rgba(26,26,26,0.35)" }}
        data-testid="about-testimonial"
      >
        <div className="grid md:grid-cols-12 gap-10">
          <p className="md:col-span-3 eyebrow" style={{ color: "rgba(26,26,26,0.55)" }}>
            Said about me
          </p>
          <div className="md:col-span-9">
            <blockquote
              className="serif-display leading-[1.1]"
              style={{ fontSize: "clamp(1.85rem, 3.4vw, 2.85rem)" }}
            >
              <span className="serif-italic">&ldquo;</span>From day one, Shivani picked up the ropes
              with incredible speed, managing multiple client projects, developing clear brand
              strategies, and turning complex reports into marketable content. Her dedication,
              quick learning, and ability to thrive under pressure make her a <span className="serif-italic">standout professional.</span>
              <span className="serif-italic">&rdquo;</span>
            </blockquote>
            <footer className="mt-8">
              <p className="serif-display text-xl">— Kriti Mukherjee</p>
              <p className="mt-2 text-[0.85rem]" style={{ color: "rgba(26,26,26,0.6)" }}>
                Fractional CMO · CEO, LUME · Independent Director, IICA
              </p>
            </footer>
          </div>
        </div>
      </section>
    </div>
  );
}
